import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Plus, Monitor, List, Printer, Settings } from 'lucide-react';
import { usePageConfig } from '../../contexts/PageConfigContext';
import { OrdersList } from './components/OrdersList';
import { useSignalR } from '../../hooks/useSignalR';
import { Order } from '../../types';
import { usePermission } from '../../hooks/usePermission';
import { Permissions } from '../../constants/permissions';

export type OrderViewMode = 'table' | 'routes';

// Page component
export const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<OrderViewMode>('table');
  const { setPageConfig } = usePageConfig();
  const printAllRef = useRef<null | (() => Promise<void>)>(null);
  
  // Permission checks
  const canCreate = usePermission(Permissions.WorkOrders.Create);
  const canViewSettings = usePermission(Permissions.Settings.View);
  
  // Ref para compartir el callback de nuevas órdenes con OrdersList
  const onOrderCreatedRef = useRef<((order: Order) => void) | null>(null);
  const onOrderUpdatedRef = useRef<((order: Order) => void) | null>(null);
  const onOrderDeletedRef = useRef<((data: { id: string }) => void) | null>(null);
  
  // Conectar a SignalR a nivel de página
  const { connectionStatus } = useSignalR({
    onOrderCreated: (order) => {
      onOrderCreatedRef.current?.(order);
    },
    onOrderUpdated: (order) => {
      onOrderUpdatedRef.current?.(order);
    },
    onOrderDeleted: (data) => {
      onOrderDeletedRef.current?.(data);
    }
  });
  
  // Callbacks para registrar los handlers de OrdersList
  const registerOrderCreatedHandler = useCallback((handler: (order: Order) => void) => {
    onOrderCreatedRef.current = handler;
  }, []);

  const registerOrderUpdatedHandler = useCallback((handler: (order: Order) => void) => {
    onOrderUpdatedRef.current = handler;
  }, []);

  const registerOrderDeletedHandler = useCallback((handler: (data: { id: string }) => void) => {
    onOrderDeletedRef.current = handler;
  }, []);

  // Update page config whenever mode changes
  // Update page config to hide global TopBar (pass empty actions but valid title to satisfy type)
  useEffect(() => {
    setPageConfig({
      title: 'Ventas', // Title required by type but won't be shown in TopBar if actions empty
      actions: [],
      connectionStatus: undefined
    });
    return () => setPageConfig({ title: 'Ventas' }); 
  }, [setPageConfig]);

  return <OrdersList 
    viewMode={viewMode}
    setViewMode={setViewMode}
    connectionStatus={connectionStatus}
    registerOrderCreatedHandler={registerOrderCreatedHandler}
    registerOrderUpdatedHandler={registerOrderUpdatedHandler}
    registerOrderDeletedHandler={registerOrderDeletedHandler}
    registerPrintAllHandler={handler => (printAllRef.current = handler)}
  />;
};
